
import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';

// Sample data for HVAC runtime
const runtimeData = [
  { time: '00:00', runtime: 20, setpoint: 68, actualTemp: 69, efficiency: 92 },
  { time: '01:00', runtime: 18, setpoint: 68, actualTemp: 68, efficiency: 95 },
  { time: '02:00', runtime: 15, setpoint: 68, actualTemp: 68, efficiency: 96 },
  { time: '03:00', runtime: 12, setpoint: 68, actualTemp: 67, efficiency: 97 },
  { time: '04:00', runtime: 10, setpoint: 68, actualTemp: 67, efficiency: 97 },
  { time: '05:00', runtime: 15, setpoint: 68, actualTemp: 67, efficiency: 96 },
  { time: '06:00', runtime: 25, setpoint: 70, actualTemp: 69, efficiency: 93 },
  { time: '07:00', runtime: 35, setpoint: 72, actualTemp: 71, efficiency: 91 },
  { time: '08:00', runtime: 45, setpoint: 73, actualTemp: 74, efficiency: 87 },
  { time: '09:00', runtime: 50, setpoint: 74, actualTemp: 75, efficiency: 85 },
  { time: '10:00', runtime: 50, setpoint: 74, actualTemp: 75, efficiency: 85 },
  { time: '11:00', runtime: 52, setpoint: 74, actualTemp: 76, efficiency: 84 },
  { time: '12:00', runtime: 55, setpoint: 74, actualTemp: 76, efficiency: 83 },
  { time: '13:00', runtime: 54, setpoint: 74, actualTemp: 75, efficiency: 84 },
  { time: '14:00', runtime: 52, setpoint: 74, actualTemp: 75, efficiency: 84 },
  { time: '15:00', runtime: 50, setpoint: 74, actualTemp: 75, efficiency: 85 },
  { time: '16:00', runtime: 48, setpoint: 74, actualTemp: 75, efficiency: 86 },
  { time: '17:00', runtime: 45, setpoint: 73, actualTemp: 74, efficiency: 87 },
  { time: '18:00', runtime: 40, setpoint: 73, actualTemp: 74, efficiency: 90 },
  { time: '19:00', runtime: 35, setpoint: 72, actualTemp: 73, efficiency: 91 },
  { time: '20:00', runtime: 30, setpoint: 72, actualTemp: 72, efficiency: 92 },
  { time: '21:00', runtime: 25, setpoint: 70, actualTemp: 71, efficiency: 93 },
  { time: '22:00', runtime: 22, setpoint: 70, actualTemp: 70, efficiency: 94 },
  { time: '23:00', runtime: 20, setpoint: 68, actualTemp: 69, efficiency: 95 },
];

const weeklyData = [
  { day: 'Monday', runtime: 580, cycles: 42, efficiency: 87 },
  { day: 'Tuesday', runtime: 540, cycles: 38, efficiency: 89 },
  { day: 'Wednesday', runtime: 600, cycles: 45, efficiency: 85 },
  { day: 'Thursday', runtime: 620, cycles: 48, efficiency: 84 },
  { day: 'Friday', runtime: 590, cycles: 43, efficiency: 86 },
  { day: 'Saturday', runtime: 480, cycles: 32, efficiency: 90 },
  { day: 'Sunday', runtime: 450, cycles: 28, efficiency: 92 },
];

const zoneData = [
  { name: 'Office Area', value: 35 },
  { name: 'Conference', value: 15 },
  { name: 'Production', value: 30 },
  { name: 'Warehouse', value: 20 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Custom dot component for recharts Line
const CustomizedDot = (props: any) => {
  const { cx, cy, value } = props;
  
  if (value > 50) {
    return (
      <svg x={cx - 6} y={cy - 6} width={12} height={12} fill="red" viewBox="0 0 12 12">
        <circle cx="6" cy="6" r="6" fill="#FF0000" stroke="#FF0000" />
      </svg>
    );
  }
  
  return (
    <svg x={cx - 3} y={cy - 3} width={6} height={6} fill="blue" viewBox="0 0 6 6">
      <circle cx="3" cy="3" r="3" />
    </svg>
  );
};

// Overview tab content
export const OverviewTab = () => (
  <div className="p-4 space-y-6">
    <div>
      <h3 className="text-lg font-medium mb-3">Daily HVAC Runtime</h3>
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={runtimeData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="time" 
              tick={{ fill: '#888', fontSize: 12 }}
              interval="preserveStartEnd"
            />
            <YAxis 
              yAxisId="left"
              tick={{ fill: '#888', fontSize: 12 }}
              label={{ value: 'Minutes per Hour', angle: -90, position: 'insideLeft' }}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#888', fontSize: 12 }}
              domain={[70, 100]}
            />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="runtime"
              yAxisId="left"
              stroke="#2563EB"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              name="Runtime (min/hr)"
            />
            <Line
              type="monotone"
              dataKey="efficiency"
              yAxisId="right"
              stroke="#10B981"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
              name="Efficiency (%)"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-sm text-gray-500 mt-2">
        HVAC system runtime in minutes per hour with efficiency percentage.
      </p>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Weekly Runtime</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="runtime" name="Total Runtime (min)" fill="#3B82F6" />
              <Bar dataKey="cycles" name="Total Cycles" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-medium mb-3">Zone Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={zoneData}
                cx="50%"
                cy="50%"
                labelLine={true}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {zoneData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  </div>
);
